import { forwardRef, useId, useState, cloneElement, useEffect } from 'react';
import { Loader2, AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import clsx from 'clsx';
import { jsxs, jsx } from 'react/jsx-runtime';

function cn(...inputs) {
  return clsx(...inputs);
}
var Button = forwardRef(function Button2({
  variant = "primary",
  size = "md",
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}, ref) {
  const isDisabled = disabled || loading;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-button",
        `ds-button--${variant}`,
        `ds-button--${size}`,
        fullWidth && "ds-button--full",
        loading && "ds-button--loading",
        className
      ),
      disabled: isDisabled,
      "aria-busy": loading || void 0,
      ...rest,
      children: [
        loading ? /* @__PURE__ */ jsx(Loader2, { className: "ds-button__spinner", "aria-hidden": "true" }) : iconLeft && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconLeft }),
        children && /* @__PURE__ */ jsx("span", { className: "ds-button__label", children }),
        iconRight && !loading && /* @__PURE__ */ jsx("span", { className: "ds-button__icon", "aria-hidden": "true", children: iconRight })
      ]
    }
  );
});
var Input = forwardRef(function Input2({ label, hint, error, prefix, suffix, size = "md", id, className, required, ...rest }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : void 0;
  const errorId = error ? `${inputId}-error` : void 0;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-input-field", `ds-input-field--${size}`, className), children: [
    label && /* @__PURE__ */ jsxs("label", { htmlFor: inputId, className: "ds-input-field__label", children: [
      label,
      required && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "ds-input-field__required", children: "*" })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn("ds-input-field__control", error && "ds-input-field__control--error"),
        "data-error": error ? "true" : void 0,
        children: [
          prefix && /* @__PURE__ */ jsx("span", { className: "ds-input-field__adornment", "aria-hidden": "true", children: prefix }),
          /* @__PURE__ */ jsx(
            "input",
            {
              ref,
              id: inputId,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy,
              "aria-required": required || void 0,
              className: "ds-input-field__input",
              ...rest
            }
          ),
          suffix && /* @__PURE__ */ jsx("span", { className: "ds-input-field__adornment", "aria-hidden": "true", children: suffix })
        ]
      }
    ),
    hint && !error && /* @__PURE__ */ jsx("p", { id: hintId, className: "ds-input-field__hint", children: hint }),
    error && /* @__PURE__ */ jsx("p", { id: errorId, className: "ds-input-field__error", role: "alert", children: error })
  ] });
});
var GlassPanel = forwardRef(function GlassPanel2({ variant = "md", as = "div", className, children, ...rest }, ref) {
  const Component = as;
  return /* @__PURE__ */ jsx(
    Component,
    {
      ref,
      "data-glass": "true",
      className: cn("ds-glass-panel", `ds-glass-panel--${variant}`, className),
      ...rest,
      children
    }
  );
});
function Spinner({ size = 16, label = "Loading\u2026", className, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      role: "status",
      "aria-live": "polite",
      "aria-label": label,
      className: cn("ds-spinner", className),
      ...rest,
      children: [
        /* @__PURE__ */ jsx(Loader2, { width: size, height: size, "aria-hidden": "true" }),
        /* @__PURE__ */ jsx("span", { className: "ds-spinner__sr", children: label })
      ]
    }
  );
}
var IconButton = forwardRef(function IconButton2({ icon, size = "md", variant = "ghost", className, type = "button", ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-icon-button",
        `ds-icon-button--${size}`,
        `ds-icon-button--${variant}`,
        className
      ),
      ...rest,
      children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "ds-icon-button__icon", children: icon })
    }
  );
});
function Tooltip({ content, children, side = "top", delayMs = 200 }) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  let timer = null;
  const show = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => setOpen(true), delayMs);
  };
  const hide = () => {
    if (timer) clearTimeout(timer);
    setOpen(false);
  };
  const trigger = cloneElement(children, {
    "aria-describedby": tipId,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide
  });
  return /* @__PURE__ */ jsxs("span", { className: "ds-tooltip-wrap", children: [
    trigger,
    /* @__PURE__ */ jsx(
      "span",
      {
        role: "tooltip",
        id: tipId,
        className: cn("ds-tooltip", `ds-tooltip--${side}`, open && "ds-tooltip--open"),
        children: content
      }
    )
  ] });
}
var ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle
};
function Toast({
  title,
  description,
  variant = "info",
  durationMs = 4e3,
  onDismiss
}) {
  const Icon = ICONS[variant];
  useEffect(() => {
    if (!durationMs || durationMs <= 0) return;
    const t = setTimeout(() => onDismiss?.(), durationMs);
    return () => clearTimeout(t);
  }, [durationMs, onDismiss]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-live": variant === "danger" ? "assertive" : "polite",
      className: cn("ds-toast", `ds-toast--${variant}`),
      children: [
        /* @__PURE__ */ jsx(Icon, { className: "ds-toast__icon", "aria-hidden": "true" }),
        /* @__PURE__ */ jsxs("div", { className: "ds-toast__body", children: [
          title && /* @__PURE__ */ jsx("div", { className: "ds-toast__title", children: title }),
          description && /* @__PURE__ */ jsx("div", { className: "ds-toast__description", children: description })
        ] }),
        onDismiss && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435",
            className: "ds-toast__dismiss",
            onClick: onDismiss,
            children: /* @__PURE__ */ jsx(X, { size: 16, "aria-hidden": "true" })
          }
        )
      ]
    }
  );
}
function Badge({ tone = "neutral", size = "sm", children, className, ...rest }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("ds-badge", `ds-badge--${tone}`, `ds-badge--${size}`, className),
      ...rest,
      children
    }
  );
}
function Kbd({ children, className, ...rest }) {
  return /* @__PURE__ */ jsx("kbd", { className: cn("ds-kbd", className), ...rest, children });
}

export { Badge, Button, GlassPanel, IconButton, Input, Kbd, Spinner, Toast, Tooltip };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map